/**
 * A call operation has either started, been updated, or finished.
 *
 * Information about ongoing call operations are stored with the call
 *    information (see the {@link call.getById} API). This event indicates that
 *    an operation's information has been changed.
 *
 * Local call operations will be tracked from start to finish. An operation may
 *    be updated as it progresses, based on the status of the operation. The
 *    operation status may be ongoing or pending, depending if the operation is
 *    waiting on activity on the local or remote end of the call, respectively.
 *
 * Except in the case of slow-start operations, remote operations will only be
 *    tracked as a "finish", to indicate that it occurred.
 * @public
 * @memberof call
 * @event call:operation
 * @param {Object} params
 * @param {string} params.operation The call operation causing this event.
 * @param {string} params.transition The transition reason for the operation change.
 * @param {boolean} params.isLocal Flag indicating whether the operation was local or not.
 * @param {Object} [params.previous] The operation information before this change.
 *    If the transition is to "start" the operation, there will be no previous information.
 * @param {string} [params.previous.operation] The operation that was ongoing.
 * @param {string} [params.previous.status] The operation status before this change.
 * @param {api.BasicError} [params.error] An error object, if the operation was not successful.
 */
export const CALL_OPERATION = 'call:operation'

/**
 * An outgoing call has been started.
 *
 * Information about the Call can be retrieved using the {@link call.getById}
 *    API.
 *
 * @public
 * @memberof call
 * @event call:start
 * @param {Object} params
 * @param {string} params.callId The ID of the call.
 * @param {api.BasicError} [params.error] An error object, if the operation was not successful.
 */
export const CALL_STARTED = 'call:start'

/**
 * A new joined call has been started.
 *
 * Information about the Call can be retrieved using the {@link call.getById}
 *    API.
 *
 * @public
 * @memberof call
 * @requires link_call
 * @event call:join
 * @param {Object} params
 * @param {string} params.callId The ID of the call.
 * @param {api.BasicError} [params.error] An error object, if the operation was not successful.
 */
export const CALL_JOIN = 'call:join'

/**
 * A new incoming call has been received.
 *
 * Information about the Call can be retrieved using the {@link call.getById}
 *    API.
 *
 * @public
 * @memberof call
 * @event call:receive
 * @param {Object} params
 * @param {string} params.callId The ID of the call.
 * @param {api.BasicError} [params.error] An error object, if the operation was not successful.
 * @example
 * client.on('call:receive', function(params) {
 *     // We have received a call, prompt the user to respond.
 *     promptUser(client.call.getById(params.callId));
 * });
 */
export const CALL_INCOMING = 'call:receive'

/**
 * A Call's state has changed.
 *
 * See {@link call.states} for information about call states.
 * @public
 * @memberof call
 * @event call:stateChange
 * @param {Object} params
 * @param {string} params.callId The ID of the Media object that was operated on.
 * @param {Object} params.previous The call's properties before the operation changed it.
 * @param {string} params.previous.state The previous state of the call.
 * @param {Object} [params.transition]
 * @param {number} [params.transition.statusCode] The status code associated with the particular state change's reason.
 * @param {string} [params.transition.reasonText] The reason for the state change.
 * @param {boolean} [params.previous.localHold] The previous local hold state. Present when the state change was a hold/unhold operation.
 * @param {boolean} [params.previous.remoteHold] The previous remote hold state. Present when the state change was a hold/unhold operation.
 * @param {api.BasicError} [params.error] An error object, if the operation was not successful.
 * @example
 * client.on('call:stateChange', function (params) {
 *     const call = client.call.getById(params.callId)
 *     const prevState = params.previous.state
 *     log(`Call changed from ${prevState} to ${call.state} state.`)
 *
 *     // Handle the event depending on the new call state.
 *     switch (call.state) {
 *         case client.call.states.CONNECTED:
 *             // Handle being on call with media.
 *             break
 *         case client.call.states.ENDED:
 *             // Handle call ending.
 *             break
 *         ...
 *     }
 * })
 */
export const CALL_STATE_CHANGE = 'call:stateChange'

/**
 * New media has been added to the call.
 * @public
 * @memberof call
 * @event call:newMedia
 * @param {Object} params
 * @param {string} params.callId The ID of the call.
 * @param {boolean} params.local Whether the new media is local or not.
 * @param {Array} params.tracks The list of new Tracks.
 * @param {string} params.mediaId The ID of the Media object the Tracks belong to.
 */
export const CALL_ADDED_MEDIA = 'call:newMedia'

/**
 * Media has been removed from the call.
 * @public
 * @memberof call
 * @event call:removedMedia
 * @param {Object} params
 * @param {string} params.callId The ID of the call.
 * @param {boolean} params.local Whether the removed Media was local or not.
 * @param {Array} params.tracks The list of removed Tracks.
 */
export const CALL_REMOVED_MEDIA = 'call:removedMedia'

/**
 * Tracks have been added to the Call after an SDK operation. Both sides of the Call
 *    are now able to render these tracks.
 *
 * Tracks are added to a Call when either the local or remote user adds new media
 *    to the Call, using the {@link call.addMedia} API for example, or when the
 *    Call is unheld with the {@link call.unhold} API.
 *
 * Remote tracks are similarly added to a Call when new tracks are added by the
 *    remote user or either user unholds the call.
 *
 * This event can indicate that multiple tracks have been removed by the same
 *    operation. For example, if the remote user added video to the call, this
 *    event would indicate a single, remote video track was added. If the local
 *    user unheld the call, this event would indicate that any tracks previously
 *    on the call have been re-added, both local and remote.
 *
 * Information about a Track can be retrieved using the {@link media.getTrackById} API.
 * @public
 * @memberof call
 * @event call:tracksAdded
 * @param {Object} params
 * @param {string} params.callId The ID of the Call the tracks were added to.
 * @param {Array<string>} params.trackIds List of track IDs that have been added to the Call.
 * @example
 * client.on('call:tracksAdded', function (params) {
 *    // Get the information for each track.
 *    const tracks = params.trackIds.map(client.media.getTrackById)
 *    tracks.forEach(track => {
 *      const { id, kind, isLocal } = track
 *      // Handle the track depending whether it is audio vs. video and local vs. remote.
 *      ...
 *    })
 * })
 */
export const CALL_TRACKS_ADDED = 'call:tracksAdded'

/**
 * Tracks have been removed from the Call after an SDK operation. The tracks may still
 *    exist, but the media is not available to both sides of the Call any longer.
 *
 * Tracks are removed from a Call when either the local or remote user stops the
 *    tracks, by using the {@link call.removeMedia} API for example, or when the
 *    Call is held with the {@link call.hold} API.
 *
 * This event can indicate that multiple tracks have been removed by the same
 *    operation. For example, if the remote user removed video from the call, this
 *    event would indicate a single, remote video track was removed. If the local
 *    user held the call, this event would indicate that all tracks on the call
 *    have been removed, both local and remote.
 *
 * Information about a Track can be retrieved using the {@link media.getTrackById} API.
 * @public
 * @memberof call
 * @event call:tracksRemoved
 * @param {Object} params
 * @param {string} params.callId The ID of the Call the tracks were removed from.
 * @param {Array<string>} params.trackIds List of track IDs that have been removed from the Call.
 * @example
 * client.on('call:tracksRemoved', function (params) {
 *    // Get the information for each track.
 *    const tracks = params.trackIds.map(client.media.getTrackById)
 *    tracks.forEach(track => {
 *      const { id, kind, isLocal } = track
 *      // Handle the track depending whether it is audio vs. video and local vs. remote.
 *      ...
 *    })
 * })
 */
export const CALL_TRACKS_REMOVED = 'call:tracksRemoved'

/**
 * Stats have been retrieved for a Call or specific Track of a Call.
 *
 * See the {@link call.getStats} API for more information.
 *
 * @public
 * @memberof call
 * @event call:statsReceived
 * @param {Object} params
 * @param {string} params.callId The ID of the Call to retrieve stats for.
 * @param {string} [params.trackId] The ID of the Track to retrieve stats for.
 * @param {Map} params.result The RTCStatsReport.
 * @param {api.BasicError} [params.error] An error object, if the operation was not successful.
 * @example
 * client.on('call:statsReceived', function (params) {
 *    // Iterate over each individual statistic inside the RTCPStatsReport Map.
 *    params.result.forEach(stat => {
 *      ...
 *    })
 * })
 */
export const STATS_RECEIVED = 'call:statsReceived'

/**
 * A local Track has been replaced by the {@link call.replaceTrack} API.
 *
 * This event is a combination of a track being removed from the Call and a new
 *    track being added to the Call. The previous Track's media is no longer
 *    available, similar to the {@link call.event:call:tracksRemoved call:tracksRemoved}
 *    event, and the new Track is available in its place, similar to the
 *    {@link call.event:call:tracksAdded call:tracksAdded} event. The event
 *    includes information about the Track that was replaced to help an application
 *    replace it seamlessly.
 * @public
 * @memberof call
 * @event call:trackReplaced
 * @param {Object} params
 * @param {string} params.callId The ID of the call where a track was replaced.
 * @param {string} [params.newTrackId] The ID of the new track that replaced the old track.
 * @param {call.TrackObject} [params.oldTrack] State of the replaced track.
 * @param {api.BasicError} [params.error] An error object, if the operation was not successful.
 * @example
 * client.on('call:trackReplaced', function (params) {
 *   const { callId, oldTrack, newTrackId } = params
 *
 *   // Unrender the removed track.
 *   handleTrackGone(oldTrack, callId)
 *
 *   // Render the added track.
 *   const track = client.media.getTrackById(newTrackId)
 *   handleTrackAdded(track, callId)
 * })
 */
export const CALL_TRACK_REPLACED = 'call:trackReplaced'

/**
 * Custom Parameters have been received for a Call.
 *
 * These are parameters set by the remote endpoint of the Call. Please refer to
 *    {@link call.CustomParameter CustomParameter} for more information.
 * @public
 * @memberof call
 * @requires link_call
 * @event call:customParameters
 * @param {Object} params
 * @param {string} params.callId The ID of the Call in which custom parameters were received.
 * @param {Array<call.CustomParameter>} params.customParameters The custom parameters received.
 */
export const CUSTOM_PARAMETERS = 'call:customParameters'

/**
 * The list of available and supported codecs by the browser have been retrieved.
 *
 * This event is emitted as a result of the {@link call.getAvailableCodecs} API. Please refer to the API for more
 *  information.
 * @public
 * @memberof call
 * @event call:availableCodecs
 * @param {Object} params
 * @param {string} params.kind The kind of media the codecs are for.
 * @param {Array<Object>} params.codecs The list of codecs.
 */
export const AVAILABLE_CODECS = 'call:availableCodecs'

/**
 * A Call's media connection state has been changed.
 *
 * This event is emitted as a result of changes to the media connection of the Call.
 * These state changes occur during call establishment, connection loss/re-establishment, call completion, etc.
 *
 * To check the media connection state of a call, retrieve the call's information using the  {@link call.getById} API,
 *  and check the `mediaConnectionState` property of the call.
 * See {@link call.mediaConnectionStates} for the list of possible values and descriptions.
 * @public
 * @memberof call
 * @event call:mediaConnectionChange
 * @param {Object} params
 * @param {string} params.callId The ID of the Call whose media connection state was changed.
 * @param {Object} params.previous The call's media connection properties before the operation changed it.
 * @param {string} params.previous.state The previous state of the media connection.
 */
export const MEDIA_CONNECTION_CHANGE = 'call:mediaConnectionChange'

/**
 * A media restart operation for a Call has been attempted.
 *
 * This event is emitted as a result of the {@link call.restartMedia} API being called.
 *    See the description for {@link call.restartMedia} for information about its
 *    usage.
 *
 * The {@link call.event:call:mediaConnectionChange call:mediaConnectionChange} event
 *    will also be emitted alongside this event when the media restart operation is
 *    successful.
 * @public
 * @static
 * @memberof call
 * @event call:mediaRestart
 * @param {Object} params
 * @param {string} params.callId The ID of the Call that was acted on.
 * @param {api.BasicError} [params.error] An error object, if the operation was not successful.
 * @example
 * client.on('call:mediaRestart', function (params) {
 *    if (params.error) {
 *      // The operation failed. May want to determine whether to re-attempt the
 *      //    operation (if the failure was simply a connectivity issue) or to
 *      //    consider the call's media irrecoverable.
 *      ...
 *    } else {
 *      // The call should be re-establishing media, with the call's
 *      //    `mediaConnectionState` being updated.
 *      const mediaState = client.call.getById(params.callId).mediaConnectionState
 *      ...
 *    }
 * })
 */
export const MEDIA_RESTART = 'call:mediaRestart'
